import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquareText, RefreshCw, Send, ShieldCheck } from "lucide-react";
import { useCommunications } from "./CommunicationsProvider";
import { useCrmIdentity } from "./lib/crmIdentity";
import {
  getSmsThread,
  markContactSmsRead,
  setContactSmsPolicy,
  sendContactSms,
  type SmsPurpose,
  type SmsThread,
} from "./lib/communications";

export type CanonicalSmsPresentation = "full" | "compact";

type Props = {
  contactId: string;
  contactName: string;
  consentSms: boolean;
  presentation: CanonicalSmsPresentation;
  isVisible: boolean;
  unreadCount?: number;
};

type ConversationState = "loading" | "idle" | "sending" | "sent" | "error";

const providerStateLabel = (state: string) =>
  state.replaceAll("_", " ").toLowerCase();

const isDeliveryFailure = (state: string) =>
  ["FAILED", "UNDELIVERED"].includes(state.toUpperCase());

export function CanonicalSmsConversation({
  contactId,
  contactName,
  consentSms,
  presentation,
  isVisible,
  unreadCount = 0,
}: Props) {
  const identity = useCrmIdentity();
  const communications = useCommunications();
  const refreshCommunications = communications.refresh;
  const [thread, setThread] = useState<SmsThread | null>(null);
  const [draft, setDraft] = useState("");
  const [state, setState] = useState<ConversationState>("loading");
  const [error, setError] = useState("");
  const [purpose, setPurpose] = useState<SmsPurpose>("POST_CONTACT_FOLLOW_UP");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago");
  const [savingPolicy, setSavingPolicy] = useState(false);
  const generation = useRef(0);
  const history = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLElement>(null);
  const readScope = useRef({ contactId, isVisible, epoch: 0 });
  if (readScope.current.contactId !== contactId || readScope.current.isVisible !== isVisible) {
    // Retire a pending read immediately on navigation, independently of draft
    // and send state. Returning to Text must perform a fresh visible read.
    readScope.current = { contactId, isVisible, epoch: readScope.current.epoch + 1 };
  }

  useEffect(() => () => { generation.current += 1; }, []);

  useEffect(() => {
    generation.current += 1;
    setThread(null);
    setDraft("");
    setState("loading");
    setError("");
  }, [contactId]);

  const loadConversation = useCallback(async () => {
    const requestGeneration = generation.current;
    const requestEpoch = readScope.current.epoch;
    const currentVisibleRead = () => requestGeneration === generation.current
      && requestEpoch === readScope.current.epoch
      && readScope.current.contactId === contactId && readScope.current.isVisible
      && document.visibilityState === "visible"
      && Boolean(panel.current?.isConnected && panel.current.getClientRects().length);
    if (!currentVisibleRead()) return;
    try {
      const next = await getSmsThread(contactId);
      if (!currentVisibleRead()) return;
      setThread(next);
      if (next.smsPolicy?.sms_timezone) setTimezone(next.smsPolicy.sms_timezone);
      setState((current) => (current === "sending" ? current : "idle"));
      setError("");
      if ((next.conversation?.unread_count || 0) > 0 && currentVisibleRead()) {
        await markContactSmsRead(contactId, currentVisibleRead);
        if (currentVisibleRead()) {
          await refreshCommunications();
        }
      }
    } catch (value) {
      if (!currentVisibleRead()) return;
      setState("error");
      setError(
        value instanceof Error ? value.message : "sms_thread_unavailable",
      );
    }
  }, [contactId, refreshCommunications]);

  useEffect(() => {
    if (!isVisible) return;
    void loadConversation();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void loadConversation();
    }, 5000);
    const focus = () => void loadConversation();
    const visibility = () => {
      readScope.current.epoch += 1;
      if (document.visibilityState === "visible") void loadConversation();
    };
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      readScope.current.epoch += 1;
      window.clearInterval(interval);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [loadConversation, isVisible]);

  useEffect(() => {
    const node = history.current;
    if (node) node.scrollTo({ top: node.scrollHeight });
  }, [thread?.messages.length]);

  const sendReply = async () => {
    const text = draft.trim();
    if (!text || !consentSms) return;
    const requestGeneration = generation.current;
    const signature = `— ${identity.displayName}, AvertSignal`;
    const signed = text.endsWith(signature) ? text : `${text}\n\n${signature}`;
    setState("sending");
    setError("");
    try {
      await sendContactSms(contactId, signed, crypto.randomUUID(), purpose);
      if (requestGeneration !== generation.current) {
        await refreshCommunications();
        return;
      }
      setDraft("");
      await Promise.all([loadConversation(), refreshCommunications()]);
      setState("sent");
    } catch (value) {
      if (requestGeneration !== generation.current) return;
      setState("error");
      setError(value instanceof Error ? value.message : "sms_send_failed");
    }
  };

  return (
    <section
      ref={panel}
      className={`canonical-sms-conversation ${presentation}`}
      aria-label={`SMS conversation with ${contactName}`}
      data-contact-id={contactId}
      data-conversation-id={thread?.conversation?.id || ""}
      data-presentation={presentation}
    >
      <header className="canonical-sms-header">
        <div>
          <MessageSquareText size={15} />
          <strong>Text conversation</strong>
        </div>
        <span>{unreadCount > 0 ? `${unreadCount} unread` : "Canonical CRM history"}</span>
      </header>
      <div
        className="sms-thread authoritative-sms-thread"
        aria-live="polite"
        ref={history}
      >
        {state === "loading" && <p>Loading conversation…</p>}
        {thread?.messages.length
          ? thread.messages.map((message) => (
              <article
                className={`sms-bubble ${message.direction.toLowerCase()} ${isDeliveryFailure(message.provider_state) ? "failed" : ""}`}
                data-message-id={message.id}
                data-provider-state={message.provider_state}
                key={message.id}
              >
                <span>
                  {message.sender_type === "AUTOMATED"
                    ? "AvertSignal automated acknowledgement"
                    : message.direction === "INBOUND"
                      ? contactName
                      : message.actor_user_id === identity.userId
                        ? "You"
                        : "AvertSignal representative"}
                </span>
                <p>{message.body}</p>
                <small>
                  {new Date(message.sent_at || message.created_at).toLocaleString()}
                  {" · "}
                  {providerStateLabel(message.provider_state)}
                  {message.provider_error_code
                    ? ` · ${message.provider_error_code}`
                    : ""}
                </small>
              </article>
            ))
          : state !== "loading" && (
              <div className="live-call-empty">
                <MessageSquareText size={20} />
                <div>
                  <strong>No messages yet</strong>
                  <span>
                    Inbound and outbound SMS for this contact will appear here.
                  </span>
                </div>
              </div>
            )}
      </div>
      <div className="sms-compose">
        <div className="toolbar-row"><label>Allowed SMS purpose<select value={purpose} onChange={event=>setPurpose(event.target.value as SmsPurpose)}><option value="POST_CONTACT_FOLLOW_UP">Post-contact follow-up</option><option value="REQUESTED_LINK">Requested link</option><option value="APPOINTMENT_COORDINATION">Appointment coordination</option><option value="REMINDER">Reminder</option><option value="ONBOARDING_COORDINATION">Onboarding coordination</option></select></label><label>Recipient timezone<input value={timezone} onChange={event=>setTimezone(event.target.value)} placeholder="America/Chicago" /></label><button type="button" className="secondary-btn" disabled={savingPolicy} onClick={()=>{setSavingPolicy(true);setContactSmsPolicy(contactId,timezone).then(()=>loadConversation()).catch(value=>setError(value instanceof Error?value.message:'sms_policy_unavailable')).finally(()=>setSavingPolicy(false))}}>{savingPolicy?"Saving…":"Confirm timezone"}</button></div>
        <textarea
          aria-label={`Text ${contactName}`}
          maxLength={1500}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={`Text ${contactName}...`}
        />
        <div className="bounded-composer-status">
          <ShieldCheck size={13} />
          <span>
            {consentSms
              ? "Recorded CRM consent is present; the server will re-check authoritative phone-level consent before sending."
              : "SMS blocked until active consent is recorded."}
          </span>
        </div>
        {error && (
          <div className="composer-error" role="alert">
            {error.replaceAll("_", " ")}
          </div>
        )}
        {state === "sent" && (
          <div className="composer-success" role="status">
            SMS accepted by the provider. Delivery status will update here.
          </div>
        )}
        <div className="sms-compose-actions">
          <button
            type="button"
            className="secondary-btn"
            disabled={state === "sending"}
            onClick={() => void loadConversation()}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={state === "sending" || !draft.trim() || !consentSms || !thread?.smsPolicy?.sms_timezone}
            onClick={() => void sendReply()}
          >
            <Send size={15} /> {state === "sending" ? "Sending…" : "Send SMS"}
          </button>
        </div>
      </div>
    </section>
  );
}
